import { getCurrentWorkKey } from './workKey';

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config',
      eventName: string,
      params?: Record<string, unknown>,
    ) => void;
  }
}

const ARTICLE_TRANSITION_KEY = 'la_article_transition_seen';
const PAGE_VIEW_KEY = 'la_last_page_view';
const ATTRIBUTION_KEY = 'la_ga_attribution';
const GA_MEASUREMENT_ID = 'G-9K61Y80X9F';

/** The resolved analytics policy, including region, saved choice and GPC. */
export function isAnalyticsEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const browser = window as unknown as Record<string, unknown>;
  const privacy = window.navigator as (Navigator & { globalPrivacyControl?: boolean }) | undefined;
  return browser[`ga-disable-${GA_MEASUREMENT_ID}`] === false && privacy?.globalPrivacyControl !== true;
}

const TOOL_SCOPED_EVENTS = new Set([
  'tool_start',
  'tool_landing_view',
  'file_upload',
  'process_complete',
  'process_failed',
  'process_abandoned',
  'export_click',
  'editor_action',
  'free_tool_completed',
  'pro_upsell_shown',
  'pro_upsell_suppressed',
  'pricing_intent',
]);

// A Stripe Checkout Session id is accepted by
// /api/stripe/claim-anonymous-checkout as a sign-in credential, so it must
// never be copied into analytics (page_path, page_location, transaction_id,
// any custom param). Keep a short, non-reusable prefix for de-duplication.
const CHECKOUT_SESSION_RE = /cs_(live|test)_[A-Za-z0-9]{10,}/g;
export function redactCheckoutSecrets(value: string): string {
  return value.replace(CHECKOUT_SESSION_RE, (m) => `${m.slice(0, 20)}…`);
}

function sanitizeParams(params: AnalyticsParams = {}) {
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, typeof value === 'string' ? redactCheckoutSecrets(value) : value]),
  );
}

function analyticsEventSuffix(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);
}

function readStoredAttribution(): AnalyticsParams {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    return raw ? JSON.parse(raw) as AnalyticsParams : {};
  } catch {
    return {};
  }
}

function storeAttribution(params: AnalyticsParams) {
  if (!Object.keys(params).length) return;
  try {
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(params));
  } catch {
    // Storage can be unavailable in strict privacy contexts. Analytics should
    // still work for the current page view without persistence.
  }
}

function attributionFromSearch(searchParams: URLSearchParams): AnalyticsParams {
  const source = searchParams.get('utm_source');
  if (!source) return {};
  const medium = searchParams.get('utm_medium') || (source === 'chatgpt.com' ? 'referral' : undefined);
  return sanitizeParams({
    campaign_source: source,
    campaign_medium: medium,
    campaign_name: searchParams.get('utm_campaign') || undefined,
    campaign_id: searchParams.get('utm_id') || undefined,
    campaign_term: searchParams.get('utm_term') || undefined,
    campaign_content: searchParams.get('utm_content') || undefined,
  });
}

function currentAttribution(): AnalyticsParams {
  if (typeof window === 'undefined') return {};

  const direct = attributionFromSearch(new URLSearchParams(window.location.search));
  if (Object.keys(direct).length) {
    storeAttribution(direct);
    return direct;
  }

  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      if (referrer.origin === window.location.origin) {
        const fromInternalUtm = attributionFromSearch(referrer.searchParams);
        if (Object.keys(fromInternalUtm).length) {
          storeAttribution(fromInternalUtm);
          return fromInternalUtm;
        }
      } else if (referrer.hostname === 'chatgpt.com') {
        const chatgptAttribution = {
          campaign_source: 'chatgpt.com',
          campaign_medium: 'referral',
          campaign_name: 'ai-referral',
        };
        storeAttribution(chatgptAttribution);
        return chatgptAttribution;
      }
    } catch {
      // Ignore malformed referrers. GA will still receive the raw page_referrer.
    }
  }

  return readStoredAttribution();
}

export function trackEvent(eventName: string, params: AnalyticsParams = {}) {
  try {
    if (typeof window === 'undefined') return;
    // Snapshot at the event, before the lazy telemetry import or another upload.
    const workKey = typeof params.work_key === 'string' ? params.work_key
      : params.work_key === null ? null : getCurrentWorkKey();
    const cleanParams = sanitizeParams(params);
    // Work identity is for the first-party ledger only, including scoped GA variants.
    delete cleanParams.work_key;
    try { window.gtag?.('event', eventName, cleanParams); } catch { /* best effort */ }

    const toolName = cleanParams.tool_name;
    if (TOOL_SCOPED_EVENTS.has(eventName) && typeof toolName === 'string' && toolName) {
      const suffix = analyticsEventSuffix(toolName);
      if (suffix) {
        try { window.gtag?.('event', `${eventName}_${suffix}`, cleanParams); } catch { /* best effort */ }
      }
    }

    // Phase-visible variants: without registered GA4 custom dimensions the
    // `phase` param is invisible in standard reports, so WHERE a run died
    // (model_download vs decode vs processing) can't be sliced. Encode it in
    // compact event names (pfail_/pabandon_ keep us under GA's 40-char limit).
    if ((eventName === 'process_failed' || eventName === 'process_abandoned')
      && typeof toolName === 'string' && toolName) {
      const phase = cleanParams.phase;
      const toolSuffix = analyticsEventSuffix(toolName);
      const phaseSuffix = typeof phase === 'string' && phase ? analyticsEventSuffix(phase) : '';
      if (toolSuffix && phaseSuffix) {
        const base = eventName === 'process_failed' ? 'pfail' : 'pabandon';
        try { window.gtag?.('event', `${base}_${toolSuffix}_${phaseSuffix}`.slice(0, 40), cleanParams); } catch { /* best effort */ }
      }
    }

    // A completed tool run is the editor's highest-investment moment — SaveNudge
    // listens for this to pitch cloud save right then, instead of on a timer
    // nobody survives (timer nudge: 1 click in 14 days; the save-button auth
    // wall converts 42/14d). Piggybacks on the analytics call so every current
    // and future tool that reports free_tool_completed participates without
    // per-panel wiring.
    if (eventName === 'free_tool_completed') {
      window.dispatchEvent(new CustomEvent('la:free-tool-completed', { detail: { toolName } }));
    }

    // Durable per-user feature ping (Supabase feature_usage, signed-in only) —
    // piggybacks on the same choke point so every tool participates without
    // per-panel wiring. Autotune Apply logs directly in AutotunePanel (it has
    // no free_tool_completed for Pro users).
    const featureSlug =
      eventName === 'free_tool_completed' && typeof toolName === 'string' && toolName
        ? `tool:${toolName}`
        : eventName === 'save_project'
          ? 'project_save'
          : eventName === 'editor_action' && cleanParams.action === 'export_completed'
            ? 'export'
            : null;
    if (featureSlug) {
      void import('./featureUsage').then((m) => m.logFeatureUsage(featureSlug, { workKey })).catch(() => undefined);
    }
    // Autotune records its explicit Apply separately; analysis alone isn't a
    // completion. Other panels capture their own input key with this event.
    if (eventName === 'process_complete' && typeof toolName === 'string' && toolName !== 'autotune') {
      void import('./featureUsage').then((m) => m.logCompletedWork(`tool:${toolName.replace(/-/g, '_')}`, workKey)).catch(() => undefined);
    }
  } catch { /* Analytics must never interrupt the tool or its result controls. */ }
}

const APP_CRASH_REPORT_CAP = 2;

/**
 * Reports an `app_crash` event, capped at APP_CRASH_REPORT_CAP sends per
 * `component` per browser session. Without this, a stuck user retry-looping
 * a broken window can emit 9-13 events from a single session, inflating the
 * daily crash count well past the number of actually-crashing users.
 *
 * The cap is tracked via a sessionStorage counter keyed per component. If
 * sessionStorage is unavailable (private browsing etc.), this falls back to
 * sending uncapped — i.e. the caller's own per-instance report guard is the
 * only thing preventing duplicates, same as before this cap existed.
 */
export function reportAppCrash(component: string, message: string, pagePath: string) {
  const key = `la_crash_reported_${component}`;
  let repeat = false;
  try {
    const count = Number(sessionStorage.getItem(key) || '0');
    if (count >= APP_CRASH_REPORT_CAP) return;
    const nextCount = count + 1;
    sessionStorage.setItem(key, String(nextCount));
    repeat = nextCount >= APP_CRASH_REPORT_CAP;
  } catch {
    // Storage unavailable — fall back to uncapped reporting (today's behavior).
  }

  trackEvent('app_crash', {
    component,
    message,
    page_path: pagePath,
    ...(repeat ? { repeat: true } : {}),
  });
}

export function trackPageView(path: string, title = document.title, params: AnalyticsParams = {}) {
  if (typeof window === 'undefined') return;
  const pagePath = path || window.location.pathname;
  const pageParams = sanitizeParams({
    page_title: title,
    page_location: window.location.href,
    page_path: pagePath,
    page_referrer: document.referrer || undefined,
    ...currentAttribution(),
    ...params,
  });

  // Update config first so the following page_view and early editor events use
  // the same page/campaign context. send_page_view stays false because the app
  // sends SPA page views explicitly. This runs even when the page_view below
  // is de-duplicated (same URL reloaded): without a configured page_location
  // gtag would fall back to document.location — with any Checkout Session id
  // in it — on every later event.
  window.gtag?.('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    ...pageParams,
  });

  const dedupeKey = `${pagePath}:${window.location.search}`;
  if (sessionStorage.getItem(PAGE_VIEW_KEY) === dedupeKey) return;
  sessionStorage.setItem(PAGE_VIEW_KEY, dedupeKey);
  window.gtag?.('event', 'page_view', pageParams);
}

export function trackToolStart(toolName: string, params: AnalyticsParams = {}) {
  trackEvent('tool_start', {
    tool_name: toolName,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...params,
  });
}

export function trackFileUpload(toolName: string, file: File, params: AnalyticsParams = {}) {
  trackEvent('file_upload', {
    tool_name: toolName,
    file_type: file.type || 'unknown',
    file_count: 1,
    file_size_bytes: file.size,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...params,
  });
}

/** Distinct events avoid treating pitch analysis as a delivered correction.
 * `source` uses the existing GA custom dimension; deviceCategory is supplied by GA.
 */
export function trackAutotuneJourney(
  step: 'analysis_started' | 'analysis_complete' | 'analysis_failed' | 'apply_attempted' | 'apply_started' | 'apply_complete' | 'apply_blocked' | 'apply_failed' | 'download',
  source: 'demo' | 'upload',
  params: AnalyticsParams = {},
) {
  trackEvent(`autotune_${step}`, {
    tool_name: 'autotune',
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...params,
    source,
  });
}

export function trackProcessComplete(toolName: string, durationSec: number, params: AnalyticsParams = {}) {
  trackEvent('process_complete', {
    tool_name: toolName,
    duration_sec: Number.isFinite(durationSec) ? Number(durationSec.toFixed(2)) : 0,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...params,
  });
}

function analyticsErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message.slice(0, 180);
  if (typeof error === 'string') return error.slice(0, 180);
  try {
    const serialized = JSON.stringify(error);
    return (serialized || 'unknown_error').slice(0, 180);
  } catch {
    return 'unknown_error';
  }
}

export function trackProcessFailed(
  toolName: string,
  phase: string,
  error: unknown,
  params: AnalyticsParams = {},
) {
  trackEvent('process_failed', {
    tool_name: toolName,
    phase,
    error_message: analyticsErrorMessage(error),
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...params,
  });
}

/**
 * User left (closed tab / navigated away / unmounted the tool) while a
 * process was still running. Fires `process_abandoned` plus the tool-suffixed
 * variant (e.g. `process_abandoned_vocal_remover`) via TOOL_SCOPED_EVENTS.
 */
export function trackProcessAbandoned(
  toolName: string,
  phase: string,
  durationMs: number,
  params: AnalyticsParams = {},
) {
  trackEvent('process_abandoned', {
    tool_name: toolName,
    phase,
    duration_ms: Math.max(0, Math.round(durationMs)),
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...params,
  });
}

export function trackEditorAction(action: string, params: AnalyticsParams = {}) {
  trackEvent('editor_action', {
    action,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...params,
  });
}

export function trackEditorHeartbeat(secondsOpen: number, params: AnalyticsParams = {}) {
  trackEvent('editor_heartbeat', {
    seconds_open: secondsOpen,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...params,
  });
}

export function trackPricingIntent(source: string, params: AnalyticsParams = {}) {
  trackEvent('pricing_intent', {
    source,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...params,
  });
}

export const PENDING_CHECKOUT_KEY = 'la_pending_checkout';

export function savePendingCheckout(params: AnalyticsParams = {}) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify({
      created_at: Date.now(),
      page_path: window.location.pathname,
      ...params,
    }));
  } catch {
    // Non-critical: checkout can still be started manually after sign-in.
  }
}

export function readPendingCheckout(): AnalyticsParams | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AnalyticsParams;
    const createdAt = typeof parsed.created_at === 'number' ? parsed.created_at : 0;
    if (!createdAt || Date.now() - createdAt > 30 * 60 * 1000) {
      sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingCheckout() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function trackArticleToolTransition(params: {
  articleSlug: string;
  targetTool: string;
  ctaPosition?: string;
}) {
  if (typeof window === 'undefined') return;
  const dedupeKey = `${params.articleSlug}:${params.targetTool}:${params.ctaPosition || 'unknown'}`;
  const raw = sessionStorage.getItem(ARTICLE_TRANSITION_KEY) || '[]';
  const seen = new Set<string>(JSON.parse(raw) as string[]);
  if (seen.has(dedupeKey)) return;
  seen.add(dedupeKey);
  sessionStorage.setItem(ARTICLE_TRANSITION_KEY, JSON.stringify([...seen]));
  trackEvent('article_tool_transition', {
    article_slug: params.articleSlug,
    target_tool: params.targetTool,
    cta_position: params.ctaPosition || 'unknown',
    page_path: window.location.pathname,
  });
}
